import { useState, type FormEvent } from 'react';

import { featureFlags } from '../featureFlags';
import MicroFrontendLoader from '../MicroFrontendLoader';
import { SUPPORTED_REGIONS, type SupportedRegion } from '../regions';

import {
  clearSession,
  parsePentestConfig,
  pentestLogin,
  readSession,
  saveSession,
  type PentestSession,
} from './pentestAuth';

const pentestConfig = parsePentestConfig(import.meta.env.VITE_STAGING_ARIES_CONFIG);
const regions = SUPPORTED_REGIONS.filter((r) => pentestConfig[r]);

const inputClass = 'border-slate-300 text-slate-700 block w-full rounded-md border bg-white p-2 text-sm';

const rateLimitMessage = (retryAfterSeconds?: number) =>
  retryAfterSeconds
    ? `Too many failed attempts. Try again in ${Math.ceil(retryAfterSeconds / 60)} minute(s).`
    : 'Too many failed attempts. Try again later.';

const LoginForm = ({ onLogin }: { onLogin: (session: PentestSession) => void }) => {
  const [region, setRegion] = useState<SupportedRegion>(regions[0]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const baseUrl = pentestConfig[region];
    if (!baseUrl) return;

    setError(null);
    setIsSubmitting(true);
    const result = await pentestLogin(baseUrl, username.trim(), password);
    setIsSubmitting(false);

    switch (result.status) {
      case 'success':
        setPassword('');
        onLogin({ username: result.username, upToken: result.upToken, region });
        return;
      case 'invalid':
        setError('Invalid username or password.');
        return;
      case 'rate_limited':
        setError(rateLimitMessage(result.retryAfterSeconds));
        return;
      default:
        setError('Sign in failed. Please try again.');
    }
  };

  return (
    <div className='bg-slate-100 flex min-h-screen items-center justify-center p-4'>
      <form
        onSubmit={handleSubmit}
        className='w-full max-w-sm space-y-4 rounded-lg bg-white p-6 shadow-sm'
        data-testid='pentest-login-form'>
        <h1 className='text-slate-700 text-xl font-medium'>Ask Aries — Pentest</h1>

        <div>
          <label htmlFor='pentest-region' className='text-slate-700 mb-1 block text-sm font-medium'>
            Region
          </label>
          <select
            id='pentest-region'
            value={region}
            onChange={(e) => setRegion(e.target.value as SupportedRegion)}
            className={inputClass}>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor='pentest-username' className='text-slate-700 mb-1 block text-sm font-medium'>
            Username
          </label>
          <input
            id='pentest-username'
            type='text'
            autoComplete='username'
            required
            maxLength={128}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor='pentest-password' className='text-slate-700 mb-1 block text-sm font-medium'>
            Password
          </label>
          <input
            id='pentest-password'
            type='password'
            autoComplete='current-password'
            required
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </div>

        {error && (
          <div
            role='alert'
            className='border-red-500 bg-red-50 text-red-800 rounded-md border-l-4 p-2 text-xs'>
            {error}
          </div>
        )}

        <button
          type='submit'
          disabled={isSubmitting}
          className='bg-slate-700 hover:bg-slate-600 w-full rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-50'>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

const PentestWorkspace = ({
  session,
  onSignOut,
}: {
  session: PentestSession;
  onSignOut: () => void;
}) => (
  <div className='bg-slate-100 flex h-screen w-full flex-col'>
    <div className='bg-slate-700 flex h-16 items-center justify-between px-4' id='unique-id'>
      <div className='text-slate-100 text-2xl'>Ask Aries</div>
      <div id='aries-trigger' className='ml-auto mr-8' />
      <div className='text-slate-100 mr-4 text-sm'>
        {session.username} · {session.region}
      </div>
      <button
        onClick={onSignOut}
        className='bg-slate-600 hover:bg-slate-500 rounded-md px-3 py-2 text-sm font-medium text-white'>
        Sign out
      </button>
    </div>
    <div className='flex-1 overflow-auto p-6'>
      <MicroFrontendLoader
        productKey='alyne_demo'
        isWidgetMode
        featureFlags={featureFlags}
        region={session.region}
        userToken={session.upToken}
      />
    </div>
  </div>
);

export const PentestPage = () => {
  const [session, setSession] = useState<PentestSession | null>(readSession);

  if (!regions.length) {
    return (
      <div className='text-slate-600 p-6'>Pentest sign-in is not configured for this build.</div>
    );
  }

  const handleLogin = (next: PentestSession) => {
    saveSession(next);
    setSession(next);
  };

  const handleSignOut = () => {
    clearSession();
    setSession(null);
  };

  return session ? (
    <PentestWorkspace session={session} onSignOut={handleSignOut} />
  ) : (
    <LoginForm onLogin={handleLogin} />
  );
};
